<template>
  <UCard
    v-if="variant === 'card' && quota?.hasSite"
    class="rounded-2xl"
    :ui="{ body: 'space-y-3 p-5' }"
  >
    <div class="flex items-start justify-between gap-3">
      <div>
        <p class="text-sm text-[var(--ui-text-muted)]">بسته پیامک</p>
        <p class="mt-1 text-lg font-bold tabular-nums text-[var(--arad-text)]">
          {{ toFaDigits(quota.remaining) }}
          <span class="text-sm font-medium text-[var(--ui-text-muted)]">
            از {{ toFaDigits(quota.allowance) }}
          </span>
        </p>
        <p class="text-sm text-[var(--ui-text-muted)]">
          {{ toFaDigits(quota.used) }} پیامک مصرف شده
        </p>
      </div>
      <UBadge
        :color="badgeColor"
        variant="subtle"
      >
        {{ badgeLabel }}
      </UBadge>
    </div>
    <div class="h-1.5 w-full overflow-hidden rounded-full bg-black/[0.08] dark:bg-white/10">
      <div
        class="h-full rounded-full transition-[width] duration-500 ease-out"
        :class="barClass"
        :style="{ width: `${remainingPercent}%` }"
      />
    </div>
    <p class="text-xs leading-5 text-[var(--ui-text-muted)]">
      هر پیامک به بیمار، شما، پزشک یا منشی از همین بسته کم می‌شود. لینک ویزیت، تخصیص فرم، پاسخ دیدگاه و اعلان پشتیبانی هر کدام ۲ پیامک حساب می‌شوند.
    </p>
  </UCard>

  <div
    v-else-if="quota?.hasSite"
    class="w-full overflow-hidden rounded-2xl bg-white/75 px-2.5 py-2 shadow-[0_1px_1px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.05)] ring-1 ring-black/[0.06] backdrop-blur-xl dark:bg-white/[0.06] dark:shadow-none dark:ring-white/10"
    :class="collapsed ? 'flex justify-center px-1.5' : ''"
    :title="collapsed ? `بسته پیامک: ${toFaDigits(quota.remaining)} از ${toFaDigits(quota.allowance)}` : undefined"
  >
    <span
      v-if="collapsed"
      class="flex size-8 items-center justify-center rounded-xl"
      :class="iconWellClass"
    >
      <UIcon
        name="i-lucide-message-square"
        class="size-4"
        :class="iconClass"
      />
    </span>
    <div
      v-else
      class="flex items-center gap-2.5"
    >
      <span
        class="flex size-8 shrink-0 items-center justify-center rounded-xl"
        :class="iconWellClass"
      >
        <UIcon
          name="i-lucide-message-square"
          class="size-4"
          :class="iconClass"
        />
      </span>
      <div class="min-w-0 flex-1">
        <div class="flex items-baseline justify-between gap-2">
          <p class="text-[11px] font-medium text-[var(--ui-text-muted)]">
            بسته پیامک
          </p>
          <p class="text-[13px] font-semibold tabular-nums tracking-tight text-[var(--arad-text)]">
            {{ toFaDigits(quota.remaining) }}
            <span class="text-[11px] font-medium text-[var(--ui-text-muted)]">
              از {{ toFaDigits(quota.allowance) }}
            </span>
          </p>
        </div>
        <div class="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-black/[0.08] dark:bg-white/10">
          <div
            class="h-full rounded-full transition-[width] duration-500 ease-out"
            :class="barClass"
            :style="{ width: `${remainingPercent}%` }"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { toFaDigits } from '~/utils/faDigits'

withDefaults(defineProps<{
  collapsed?: boolean
  variant?: 'card' | 'compact'
}>(), {
  collapsed: false,
  variant: 'compact',
})

const { data } = await useFetch<{
  hasSite: boolean
  allowance: number
  used: number
  remaining: number
}>('/api/me/sms-quota', { key: 'sms-quota' })

const quota = computed(() => data.value)

/** Fill shows what is left: a full package is a full bar. */
const remainingPercent = computed(() => {
  const allowance = quota.value?.allowance ?? 0
  if (!allowance) return 0
  return Math.min(100, Math.max(0, Math.round(((quota.value?.remaining ?? 0) / allowance) * 100)))
})

const level = computed(() => {
  const left = remainingPercent.value
  if (left >= 80) return 'full' as const
  if (left >= 60) return 'high' as const
  if (left >= 40) return 'mid' as const
  if (left >= 20) return 'low' as const
  return 'empty' as const
})

const barClass = computed(() => {
  if (level.value === 'full') return 'bg-green-700'
  if (level.value === 'high') return 'bg-green-400'
  if (level.value === 'mid') return 'bg-yellow-400'
  if (level.value === 'low') return 'bg-orange-500'
  return 'bg-red-500'
})

const iconWellClass = computed(() => {
  if (level.value === 'full') return 'bg-green-600/12'
  if (level.value === 'high') return 'bg-green-500/12'
  if (level.value === 'mid') return 'bg-yellow-400/18'
  if (level.value === 'low') return 'bg-orange-500/14'
  return 'bg-red-500/12'
})

const badgeColor = computed(() => {
  if (level.value === 'empty') return 'error' as const
  if (level.value === 'low' || level.value === 'mid') return 'warning' as const
  return 'success' as const
})

const badgeLabel = computed(() => {
  if (level.value === 'empty') return 'تمام شده'
  if (level.value === 'low') return 'رو به اتمام'
  return 'فعال'
})

const iconClass = computed(() => {
  if (level.value === 'full') return 'text-green-600'
  if (level.value === 'high') return 'text-green-500'
  if (level.value === 'mid') return 'text-yellow-500'
  if (level.value === 'low') return 'text-orange-500'
  return 'text-red-500'
})
</script>
